<template>
  <div class="container-fluid mt-4">
    <div class="d-flex justify-content-between align-items-center mb-5">
      <h2 class="fw-bold text-dark mb-0">Cruise Lines <span class="text-muted fs-5">({{ cruiseLines.length }})</span></h2>
      <button class="btn btn-primary-gradient px-4 rounded-pill shadow-sm" @click="openAddModal">
        <i class="fa fa-plus me-2"></i> Add Cruise Line
      </button>
    </div>

    <div class="row g-4">
      <div class="col-sm-6 col-md-4 col-lg-3" v-for="cruiseLine in cruiseLines" :key="cruiseLine.id">
        <div class="glass-panel text-center p-4 position-relative hover-lift d-flex flex-column h-100">
          <div class="flex-grow-1 d-flex align-items-center justify-content-center mb-4" style="min-height: 120px;">
            <img v-if="cruiseLine.attributes.logo.data"
                :src="getFullImageUrl(cruiseLine.attributes.logo.data.attributes.url)"
                :alt="cruiseLine.attributes.logo.data.attributes.alternativeText"
                class="img-fluid" style="max-height: 80px; object-fit: contain;" />
            <div v-else class="text-muted small">No Logo</div>
          </div>
          
          <h5 class="fw-bold mb-4 text-dark">{{ cruiseLine.attributes.name }}</h5>
          
          <div class="d-flex justify-content-center mt-auto">
            <button class="btn btn-light btn-sm rounded-pill text-primary me-2 px-3 fw-bold"
                @click="editCruiseLine(cruiseLine)">
                <i class="fa fa-pen me-1"></i> Edit
            </button>
            <button class="btn btn-light btn-sm rounded-pill text-danger px-3 fw-bold"
                @click="deleteCruiseLine(cruiseLine.id)">
                <i class="fa fa-trash me-1"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <b-modal id="cruise-line-modal" :title="isEditing ? 'Edit Cruise Line' : 'Add Cruise Line'" hide-footer centered header-class="border-0 pb-0" body-class="p-4" content-class="border-0 shadow-lg rounded-4">
      <form @submit.prevent="handleSubmit">
        <div class="mb-4">
          <label class="form-label text-muted small fw-bold mb-2">Cruise Line Name</label>
          <input type="text" v-model="form.name" class="form-control bg-light border-0" required placeholder="e.g. Royal Caribbean" />
        </div>
        
        <div class="mb-5">
          <label class="form-label text-muted small fw-bold mb-2">Brand Logo</label>
          <div v-if="form.logo && typeof form.logo === 'string'" class="mb-3 text-center p-3 rounded" style="background-color: #F8F9FC; border: 1px dashed #E2E2EA;">
              <img :src="getFullImageUrl(form.logo)" style="max-height: 60px; object-fit: contain;" />
          </div>
          <input type="file" @change="handleFileUpload" class="form-control bg-light border-0" />
        </div>
        
        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-light rounded-pill px-4 me-3 fw-bold" @click="$bvModal.hide('cruise-line-modal')">Cancel</button>
          <button type="submit" class="btn btn-primary-gradient rounded-pill px-4 shadow-sm" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ isEditing ? 'Save Changes' : 'Create Cruise Line' }}
          </button>
        </div>
      </form>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal id="delete-confirm-modal" title="Confirm Deletion" hide-footer centered header-class="border-0 pb-0" body-class="p-4 text-center" content-class="border-0 shadow-lg rounded-4">
      <div class="mb-4">
        <i class="fa fa-exclamation-triangle text-danger mb-3" style="font-size: 3rem;"></i>
        <h4 class="fw-bold text-dark mb-2">Delete Cruise Line?</h4>
        <p class="text-muted">This action cannot be undone. Are you sure you want to permanently delete this cruise line?</p>
      </div>
      <div class="d-flex justify-content-center gap-3 mt-4">
        <button type="button" class="btn btn-light rounded-pill px-4 fw-bold" @click="$bvModal.hide('delete-confirm-modal')">Cancel</button>
        <button type="button" class="btn btn-danger rounded-pill px-4 fw-bold shadow-sm" @click="confirmDelete" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Yes, Delete
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import config from '@lib/frontend/config';

export default {
  data() {
    return {
      cruiseLines: [],
      isEditing: false,
      loading: false,
      cruiseLineToDelete: null,
      form: {
        id: null,
        name: '',
        logo: null
      }
    };
  },
  created() {
    this.fetchCruiseLines();
  },
  methods: {
    async fetchCruiseLines() {
      try {
        const response = await this.$xhr.get("/api/cruise-lines?populate=*");
        this.cruiseLines = response.data.data;
      } catch (error) {
        console.error("Error fetching cruise lines:", error);
      }
    },
    getFullImageUrl(partialUrl) {
      if (!partialUrl) return '';
      return config.buildUrl('api', partialUrl);
    },
    handleFileUpload(event) {
      if (event.target.files.length > 0) {
        this.form.logo = event.target.files[0];
      }
    },
    openAddModal() {
      this.resetForm();
      this.$bvModal.show('cruise-line-modal');
    },
    editCruiseLine(cruiseLine) {
      this.isEditing = true;
      this.form.id = cruiseLine.id;
      this.form.name = cruiseLine.attributes.name;
      this.form.logo = cruiseLine.attributes.logo?.data?.attributes?.url || null;
      this.$bvModal.show('cruise-line-modal');
    },
    async handleSubmit() {
      this.loading = true;
      try {
        const formData = new FormData();
        formData.append('data', JSON.stringify({ name: this.form.name }));
        
        if (this.form.logo && typeof this.form.logo !== 'string') {
          formData.append('files.logo', this.form.logo);
        }

        if (this.isEditing) {
          await this.$xhr.put(`/api/cruise-lines/${this.form.id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          this.$bvToast.toast('Cruise line updated successfully!', { title: 'Success', variant: 'success', solid: true });
        } else {
          await this.$xhr.post("/api/cruise-lines", formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          this.$bvToast.toast('Cruise line added successfully!', { title: 'Success', variant: 'success', solid: true });
        }
        
        await this.fetchCruiseLines();
        this.$bvModal.hide('cruise-line-modal');
        this.resetForm();
      } catch (error) {
        console.error("Submit error:", error);
        this.$bvToast.toast('An error occurred.', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },
    deleteCruiseLine(id) {
      this.cruiseLineToDelete = id;
      this.$bvModal.show('delete-confirm-modal');
    },
    async confirmDelete() {
      if (!this.cruiseLineToDelete) return;
      
      this.loading = true;
      try {
        await this.$xhr.delete(`/api/cruise-lines/${this.cruiseLineToDelete}`);
        this.$bvToast.toast('Cruise line deleted!', { title: 'Deleted', variant: 'warning', solid: true });
        await this.fetchCruiseLines();
        this.$bvModal.hide('delete-confirm-modal');
      } catch (error) {
        console.error("Delete error:", error);
        this.$bvToast.toast('Could not delete.', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
        this.cruiseLineToDelete = null;
      }
    },
    resetForm() {
      this.isEditing = false;
      this.form.id = null;
      this.form.name = '';
      this.form.logo = null;
      
      // Reset file input in DOM if exists
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    }
  }
};
</script>

<style scoped>
.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.08) !important;
}

.btn-light {
  background-color: #F8F9FC;
  border: 1px solid #E2E2EA;
}

.btn-light:hover {
  background-color: #E2E2EA;
}

.rounded-4 {
  border-radius: 20px !important;
}
</style>